/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import MenuIcon from '@material-ui/icons/Menu';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
    Toolbar, Typography, Button, IconButton, CssBaseline, AppBar, Box,
} from '@material-ui/core';

import UserMenu from './UserMenu';
import { isSelfSignupEnabled, localStoreOperation, sessionStoreOperation } from '../../../data/utils';
import {
    CLAIMS_FULL_NAME, CLAIMS_EMAIL, CLAIMS_GOOGLE_PIC, CLAIMS_GITHUB_PIC,
    CLAIMS_PHOTO_URL, CLAIMS_AVATAR_URL, RETURN_TO_PATH, RESOURCE_SIGN_IN, PATH_SEPARATOR, RESOURCE_API,
    RESOURCE_APPLICATIONS, CLAIMS_PICTURE, FIDP, SELECTED_ORG_HANDLE_NAME
} from '../../../data/constants';
import {
    ApplicationState,
    CustomDomainOrganizationMappingReducer,
    Organization,
    StorageOperations,
    UserReducer
} from '../../../data/interfaces';
import { obtainApimTokenAndChangeOrganization } from '../../../data/operations';
import {trackApiButtonClickOnHeader, trackApplicationClickOnHeader} from "../../../util/app-insights";
import Settings from '../../../conf/settings';

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
    },
    toolbar: {
        paddingRight: 24, // keep right padding when drawer closed
    },
    appBarLogo: {
        ...theme.custom.appBar.logo.styles,
        maxHeight: '50px',
    },
    rectangle: {
        height: '32px',
        width: '1px',
        opacity: 0.13,
        backgroundColor: '#40404B',
    },
    appBar: {
        ...theme.custom.appBar.styles,
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        boxShadow: 'inset 0 -1px 0 0 #E1E2E7, 0 1px 4px 0 rgba(0,0,0,0.08)'
    },
    appBarShift: {
        marginLeft: drawerWidth,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    menuButton: {
        marginRight: 24,
    },
    menuButtonHidden: {
        display: 'none',
    },
    title: {
        ...theme.custom.appBar.title.styles
    },
    signInButton: {
        ...theme.custom.appBar.signInButton.styles
    },
    navigationButton: {
        ...theme.custom.appBar.menuButtons
    },
    navigationButtonSelected: {
        ...theme.custom.appBar.menuButtonsSelected
    },
    menuLogoTitle: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        minWidth: theme.spacing(80),
        [theme.breakpoints.down('sm')]: {
            minWidth: 'auto'
        }
    },
    menuNavigation: {
        textTransform: 'capitalize',
        flexGrow: 2
    },
    menuUser: {
        flexGrow: 2,
        textAlign: 'end'
    },
    orgSelect: {
        height: theme.spacing(4),
        color: "#5567d5",
        fontSize: "13px",
        fontFamily: "Gilmer",
        fontWeight: "bold",
        lineHeight: theme.spacing(3),
        marginLeft: theme.spacing(2),
        ...theme.custom.appBar.orgSelect
    },
    orgSelectMenu: {
        height: theme.spacing(4),
        padding: theme.spacing(1),
        display: 'flex',
        alignItems: 'center'
    },
    orgSelectMenuPlaceholder: {
        fontStyle: 'italic'
    }
}));

interface MainAppBarProps {
    showDrawerButton?: boolean,
    userReducer: UserReducer,
    customDomainOrganizationMappingReducer: CustomDomainOrganizationMappingReducer,
    menuDrawerOpen: boolean,
    setMenuDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>
    obtainApimTokenAndChangeOrganization: (orgHandle: Organization) => void
}

function MainAppBar(props: MainAppBarProps) {
    const {
        showDrawerButton, menuDrawerOpen, setMenuDrawerOpen,
    } = props;
    const { user, userInfo, apimTokenObtained, orgs: userOrganizations } = props.userReducer;
    const allowedScopes = user.allowedScopes;
    const classes = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const { pathname } = useLocation();
    const { orgName } = useParams<{orgName: string}>();
    const intl = useIntl();

    let userFullName = '';
    let userEmail = '';
    let profileImage = "";

    if (userInfo) {
        userFullName = !userInfo[CLAIMS_FULL_NAME] || userInfo[CLAIMS_FULL_NAME] == 'null'
        ? user.displayName || user.email : userInfo[CLAIMS_FULL_NAME]
        userEmail = userInfo[CLAIMS_EMAIL] || user.email;

        if (userInfo[CLAIMS_GOOGLE_PIC]) {
            profileImage = userInfo[CLAIMS_GOOGLE_PIC];
        } else if (userInfo[CLAIMS_GITHUB_PIC]) {
            profileImage = userInfo[CLAIMS_GITHUB_PIC];
        } else if (userInfo[CLAIMS_PHOTO_URL]) {
            profileImage = userInfo[CLAIMS_PHOTO_URL];
        } else if (userInfo[CLAIMS_AVATAR_URL]) {
            profileImage = userInfo[CLAIMS_AVATAR_URL];
        } else if (userInfo[CLAIMS_PICTURE]) {
            profileImage = userInfo[CLAIMS_PICTURE];
        } else {
            profileImage = "";
        }
    }

    useEffect(() => {
      if (apimTokenObtained) {
        localStoreOperation(
          StorageOperations.ADD,
          SELECTED_ORG_HANDLE_NAME,
          orgName,
        );
      }
      if (
        !isSelfSignupEnabled(orgName) &&
        orgName !==
          localStoreOperation(
            StorageOperations.GET,
            SELECTED_ORG_HANDLE_NAME,
          ) &&
        localStoreOperation(StorageOperations.GET, FIDP) ===
          Settings.enterpriseFidp
      ) {
        localStoreOperation(StorageOperations.REMOVE, FIDP);
      }
    }, []);

    const handleSignInClick = () => {
        sessionStoreOperation(StorageOperations.ADD, RETURN_TO_PATH, window.location.pathname);
        if (isSelfSignupEnabled(orgName)) {
            localStoreOperation(StorageOperations.ADD, SELECTED_ORG_HANDLE_NAME, orgName);
            localStoreOperation(StorageOperations.ADD, FIDP, Settings.enterpriseFidp);
        }
        history.replace(RESOURCE_SIGN_IN);
    }

    const handleMenuButtonClick = () => {
        setMenuDrawerOpen(prevState => !prevState);
    }

    const handleNavigationTo = (path: string) => {
        history.push(path);
    }

    const isCurrentPath = (path: string): boolean => {
        return path === pathname || path + PATH_SEPARATOR === pathname;
    }

    const hasUserAccessToCurrentPathOrg = (): boolean => {
        const selectedOrganization = userOrganizations.find(userOrg => userOrg.handle === orgName);
        return !!selectedOrganization;
    }

    return (
        <div className={classes.root}>
            <CssBaseline />
            <AppBar position="absolute" color="inherit" className={clsx(classes.appBar, menuDrawerOpen && classes.appBarShift)}>
                <Toolbar className={classes.toolbar}>
                    <Box className={classes.menuLogoTitle}>
                        {showDrawerButton &&
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                onClick={handleMenuButtonClick}
                                className={clsx(classes.menuButton)}
                            >
                                <MenuIcon />
                            </IconButton>
                        }
                        <img
                            src={theme.custom.appBar.logo.src ?? "/themes/default/images/logo-black.svg"}
                            alt={
                                intl.formatMessage({
                                    id: "mainAppBar.logo.altText",
                                    defaultMessage: "Choreo logo"
                                })
                            }
                            className={classes.appBarLogo}
                            onClick={() => {
                                handleNavigationTo(PATH_SEPARATOR + orgName);
                            }}
                        />
                        <div className={classes.rectangle}></div>
                        <Typography noWrap className={classes.title}>
                            {theme.custom.appBar.title.text ?? intl.formatMessage({
                                id: "mainAppBar.title.devPortal",
                                defaultMessage: "API Developer Portal"
                            })}
                        </Typography>
                    </Box>
                        <Box className={classes.menuNavigation}>
                            <Button
                                data-testid="home-appbar-btn"
                                variant="text"
                                className={clsx(classes.navigationButton,
                                    isCurrentPath(PATH_SEPARATOR + orgName) &&
                                    classes.navigationButtonSelected)}
                                onClick={() => { handleNavigationTo(PATH_SEPARATOR + orgName) }}
                            >
                                <FormattedMessage
                                    id="mainAppBar.links.home"
                                    defaultMessage="Home"
                                />
                            </Button>
                            <Button
                                variant="text"
                                className={clsx(classes.navigationButton,
                                    isCurrentPath(PATH_SEPARATOR + orgName + RESOURCE_API) &&
                                    classes.navigationButtonSelected)}
                                onClick={() => {
                                    handleNavigationTo(PATH_SEPARATOR + orgName + RESOURCE_API) ;
                                    trackApiButtonClickOnHeader();
                                }}
                                data-testid='apis-appbar-btn'
                            >
                                <FormattedMessage
                                    id="mainAppBar.links.apis"
                                    defaultMessage="APIs"
                                />
                            </Button>
                            {/* Showing Applications links only when token exchange is successful and who have apim:subscribe permission */}
                            {apimTokenObtained && allowedScopes.includes('apim:subscribe') && hasUserAccessToCurrentPathOrg() && (
                            <Button
                                data-testid="applications-appbar-btn"
                                variant="text"
                                className={clsx(classes.navigationButton, isCurrentPath(PATH_SEPARATOR + orgName + RESOURCE_APPLICATIONS)
                                    && classes.navigationButtonSelected)}
                                onClick={() => {
                                    handleNavigationTo(PATH_SEPARATOR + orgName + RESOURCE_APPLICATIONS);
                                    trackApplicationClickOnHeader()
                                }}
                            >
                                <FormattedMessage
                                    id="mainAppBar.links.applications"
                                    defaultMessage="Applications"
                                />
                            </Button>
                            )}
                        </Box>
                    <Box className={classes.menuUser}>
                        {apimTokenObtained && hasUserAccessToCurrentPathOrg()?
                            <UserMenu userFullName={userFullName} userEmail={userEmail} profileImage={profileImage} />
                            :
                            <Button
                                color="inherit"
                                onClick={handleSignInClick}
                                className={classes.signInButton}
                                data-testid="login-button"
                            >
                                <Typography>
                                    <FormattedMessage
                                        id="mainAppBar.userMenu.loginSignUp"
                                        defaultMessage="Login / Sign Up"
                                    />
                                </Typography>
                            </Button>
                        }
                    </Box>
                </Toolbar>
            </AppBar>
        </div>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        userReducer: state.userReducer,
        customDomainOrganizationMappingReducer: state.customDomainOrganizationMappingReducer,
    }
}

const mapDispatchToProps = {
    obtainApimTokenAndChangeOrganization: obtainApimTokenAndChangeOrganization
}

export default connect(mapStateToProps, mapDispatchToProps)(MainAppBar);
